// client/src/components/unidades/tabs/HorarioAtendimentoTab.js
import React, { useState, useMemo } from 'react';
import { useHorarios, useMutateHorarios } from '../../../hooks/useHorarios';
import { Clock, PlusCircle, ArrowLeft, Loader2, Info, RefreshCcw } from 'lucide-react';
// Reaproveitando os componentes já criados na pasta de configurações
import HorarioForm from '../../configuracoes/horarios/HorarioForm';
import HorarioRow from '../../configuracoes/horarios/HorarioRow';

// ✅ FUNÇÃO BLINDADA PARA EXTRAIR ID
const getSafeId = (item) => {
    if (!item) return null;
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
        if (item._id) return String(item._id);
        if (item.id) return String(item.id);
    }
    return String(item);
};

const HorarioAtendimentoTab = ({ unidade, isCreating = false }) => {
    const targetUnidadeId = getSafeId(unidade);

    // Estados de controle da interface
    const [showForm, setShowForm] = useState(false);
    const [currentHorario, setCurrentHorario] = useState(null);

    // Buscar horários APENAS desta unidade
    const paramsBuscaHorarios = useMemo(() => ({
        tipoEntidade: 'Unidade',
        entidadeAgendavel: targetUnidadeId,
    }), [targetUnidadeId]);

    const { 
        data: horariosData, 
        isLoading: horarioLoading, 
        refetch: refetchHorarios, 
        isFetching 
    } = useHorarios(paramsBuscaHorarios, { 
        enabled: !!targetUnidadeId 
    });

    const { deleteHorario } = useMutateHorarios();
    const isMutating = deleteHorario.isPending;

    // Processar dados
    const horarios = horariosData || [];
    const horariosSemanais = useMemo(() => Array.isArray(horarios) ? horarios.filter(h => h.tipo === 'SEMANAL') : [], [horarios]);
    const datasEspecificas = useMemo(() => Array.isArray(horarios) ? horarios.filter(h => h.tipo === 'DATA_UNICA') : [], [horarios]);

    // Formatando a unidade atual para passar pro Form (Garante auto-seleção)
    const unidadesDisponiveis = useMemo(() => {
        if (!unidade) return [];
        return [{
            _id: targetUnidadeId,
            nome: unidade.nome
        }];
    }, [unidade, targetUnidadeId]);

    // Handlers
    const handleToggleForm = () => { 
        if (showForm) setCurrentHorario(null); 
        setShowForm(p => !p); 
    };
    
    const handleEdit = (horario) => { 
        setCurrentHorario(horario); 
        setShowForm(true); 
    };
    
    const handleDelete = (horarioId) => { 
        if(window.confirm('Tem certeza que deseja excluir este horário?')) {
            deleteHorario.mutate(horarioId); 
        }
    };

    // Bloqueio se a unidade ainda não foi salva (criação)
    if (isCreating || !targetUnidadeId) {
        return (
            <div className="form-content">
                <div className="form-header">
                    <div className="flex-1">
                        <h3 className="form-title flex items-center mb-0">
                            <Clock size={20} className="inline mr-2 text-primary" />
                            Horários de Atendimento
                        </h3>
                    </div>
                </div>
                <div className="form-tabs-content">
                    <div className="panel-state-wrapper panel-info">
                        <Info size={24} className="icon"/>
                        <span>
                            Primeiro, salve a unidade na aba 'Dados da Unidade'. Após salvar, as opções para configurar os horários de funcionamento aparecerão aqui.
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="form-content" disabled={isMutating}>
            <div className="form-header flex items-center justify-between mb-4">
                <div className="flex-1">
                    <h3 className="form-title flex items-center mb-0">
                        <Clock size={20} className="inline mr-2 text-primary" />
                        Horários de Atendimento: {unidade.nome}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Defina os dias e horários de funcionamento geral desta unidade.
                    </p>
                </div>
                {!showForm && (
                    <div className="flex gap-2">
                        <button 
                            type="button" 
                            onClick={() => refetchHorarios()} 
                            className="action-button action-button-secondary" 
                            title="Atualizar Lista"
                            disabled={isFetching}
                        >
                            <RefreshCcw size={16} className={`icon ${isFetching ? "animate-spin" : ""}`} />
                            Atualizar
                        </button>
                        <button
                            type="button"
                            onClick={handleToggleForm}
                            className="action-button action-button-primary"
                        >
                            <PlusCircle size={18} className="icon" />
                            Adicionar Regra
                        </button>
                    </div>
                )}
                {showForm && (
                    <button
                        type="button"
                        onClick={handleToggleForm}
                        className="action-button action-button-secondary text-gray-600"
                    >
                        <ArrowLeft size={16} className="icon" /> Voltar para lista
                    </button>
                )}
            </div>

            <div className="form-tabs-content p-0">
                {!showForm ? (
                    <div className="table-wrapper border border-gray-200 rounded-lg overflow-hidden mt-4">
                        {horarioLoading ? (
                            <div className="flex justify-center items-center py-12">
                                <Loader2 size={32} className="animate-spin text-primary" />
                            </div>
                        ) : (
                            <table className="styled-table w-full">
                                <thead>
                                    <tr>
                                        <th>Dia / Data</th>
                                        <th>Tipo</th>
                                        <th>Início</th>
                                        <th>Fim</th>
                                        <th>Intervalos (Pausas)</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {horariosSemanais.length > 0 && horariosSemanais.map(h => (
                                        <HorarioRow 
                                            key={h._id} 
                                            horario={h} 
                                            onEdit={handleEdit} 
                                            onDelete={handleDelete} 
                                            isMutating={isMutating} 
                                        />
                                    ))}
                                    
                                    {datasEspecificas.length > 0 && datasEspecificas.map(h => (
                                        <HorarioRow 
                                            key={h._id} 
                                            horario={h} 
                                            onEdit={handleEdit} 
                                            onDelete={handleDelete} 
                                            isMutating={isMutating} 
                                        />
                                    ))}
                                    
                                    {(!horarios || horarios.length === 0) && (
                                        <tr>
                                            <td colSpan="6" className="text-center py-8 text-gray-500 bg-gray-50">
                                                <Clock size={32} className="mx-auto text-gray-300 mb-2" />
                                                Nenhum horário cadastrado para esta unidade.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                ) : (
                    <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-6 relative">
                        {/* Reutilizando o formulário exato da página de configurações globais */}
                        <HorarioForm 
                            entidadeId={targetUnidadeId}
                            tipoEntidade="Unidade"
                            onCloseForm={handleToggleForm} 
                            current={currentHorario}
                            clearCurrent={() => setCurrentHorario(null)}
                            horarios={horarios}
                            unidadesDisponiveis={unidadesDisponiveis} // Passa só essa unidade para auto-selecionar
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default HorarioAtendimentoTab;